import { useState } from 'react';
import { ArrayField } from 'informed';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { actions } from 'store';
import { Button, ModalButtonWithForm } from 'components/common';
import { FormGroup, Select } from 'components/common/form';
import { ProductUserCustomAttributeRenderFormField } from 'components/productUser';
import { UsersListSelectedUsers } from 'components/users';

import type { CustomAttributes } from 'shared-types';
import type { CalendarUser } from './CalendarEventParticipants';

interface Props {
  requestId?: number;
  selectedUserIds?: number[];
  updateUsers: () => Promise<void>;
  user?: Pick<CalendarUser, 'id' | 'userAttributes'>;
}

const CalendarEventParticipantsEditCustomAttributes = ({
  requestId,
  selectedUserIds,
  updateUsers,
  user
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    language,
    messages,
    pageState: { calendarEvent }
  } = useReduxState();

  const [userIds, setUserIds] = useState<number[]>([]);

  const handleSubmit = async (
    values: CustomAttributes & { userAttributes?: Record<'id', number>[] }
  ) => {
    const userAttributes = {};

    Object.keys(values).forEach((fieldKey) => {
      if (fieldKey.startsWith('userAttributes-')) {
        const attributeId = fieldKey.split('-')[1];
        userAttributes[attributeId] = values[fieldKey];
      }
    });

    if (requestId) {
      await api.post(
        `/admin/calendar-events/${calendarEvent.id}/request-access/${requestId}`,
        { userAttributes }
      );
    } else {
      await api.put(
        `/admin/calendar-events/${calendarEvent.id}/users-custom-attributes`,
        {
          userAttributes: values.hasOwnProperty('userAttributes')
            ? values.userAttributes.reduce((allFields, field) => {
                allFields[field.id] =
                  values?.[`userAttributes-${field.id}`] || '';
                return allFields;
              }, {})
            : userAttributes,
          userIds: user ? [user.id] : userIds
        }
      );
    }

    await updateUsers();

    dispatch(
      actions.addNotice({
        text: messages.eventCalendarParticipantDataEditSuccess,
        type: 'success'
      })
    );
  };

  const initialValues = user
    ? calendarEvent.userAttributes?.reduce((allFields, field) => {
        allFields[`userAttributes-${field.id}`] =
          user.userAttributes?.[field.id];
        return allFields;
      }, {})
    : { userAttributes: [{ id: 0 }] };

  const title = user
    ? messages.eventCalendarParticipantDataEdit
    : messages.eventCalendarParticipantDataEditForUsers;

  return calendarEvent.userAttributes?.length ? (
    <ModalButtonWithForm
      buttonCategory="success"
      buttonIcon="edit"
      buttonIsDropdown={true}
      buttonText={title}
      initialValues={initialValues}
      modalActionText={messages.commonUpdate}
      modalTitle={title}
      modalWidth="medium"
      onSubmit={handleSubmit}
      pristineIsValid={false}
    >
      {() => (
        <>
          {!user && selectedUserIds?.length ? (
            <UsersListSelectedUsers
              allowGuestUsers={true}
              allowSelf={true}
              onLoadedUsers={(users) =>
                setUserIds(users.map((user) => user.id))
              }
              userSelectionParams={{ selectedUserIds }}
            />
          ) : null}

          {user ? (
            calendarEvent.userAttributes.map((attr) => {
              const renderTitle =
                attr.titleTranslations?.[language] || attr.title;

              return (
                <FormGroup key={attr.id} label={renderTitle}>
                  <ProductUserCustomAttributeRenderFormField
                    attribute={attr}
                    ignoreRequired={true}
                  />
                </FormGroup>
              );
            })
          ) : (
            <FormGroup label={messages.eventCalendarParticipantDataEditSelect}>
              <ArrayField field="userAttributes">
                {({ add }) => (
                  <>
                    <ArrayField.Items<{ id: number }>>
                      {({ remove, field, index, values }) => {
                        const attribute = calendarEvent.userAttributes?.find(
                          (attr) => attr.id === values?.id
                        );

                        return (
                          <div className="attribute">
                            <Select
                              field={`${field}.id`}
                              id={`edit-user-attributes-attribute-key-${index}`}
                              options={calendarEvent.userAttributes.map(
                                (attr) => ({
                                  label: attr.title,
                                  value: attr.id
                                })
                              )}
                              style={{ width: 200 }}
                            />

                            {attribute ? (
                              <>
                                <div className="attribute-field">
                                  <ProductUserCustomAttributeRenderFormField
                                    attribute={attribute}
                                    ignoreRequired={true}
                                  />
                                </div>

                                <div className="attribute-actions">
                                  <Button
                                    aria-label={messages.commonDelete}
                                    category="light"
                                    data-tooltip={messages.commonDelete}
                                    disabled={index === 0}
                                    icon="trash-alt"
                                    onClick={remove}
                                    size="small"
                                  />
                                </div>
                              </>
                            ) : null}

                            <style jsx>{`
                              .attribute {
                                align-items: center;
                                display: flex;
                                flex-direction: row;
                                gap: 10px;
                                margin-top: 5px;
                                width: 100%;
                              }
                              .attribute:not(:first-child) {
                                margin-top: 10px;
                              }
                              .attribute-field {
                                flex-grow: 1;
                              }
                            `}</style>
                          </div>
                        );
                      }}
                    </ArrayField.Items>

                    <Button
                      category="light"
                      icon="plus"
                      onClick={add}
                      size="small"
                      style={{ marginTop: 15 }}
                      text={messages.commonAddField}
                    />
                  </>
                )}
              </ArrayField>
            </FormGroup>
          )}
        </>
      )}
    </ModalButtonWithForm>
  ) : null;
};

export default CalendarEventParticipantsEditCustomAttributes;
